<template>
  <div class="account-page">
    <DashboardPageHeader
      title="Account"
      subtitle="Manage how you sign in and the link your customers use."
    />

    <div v-if="loading" class="account-loading">
      <BrandedLoader text="Loading account…" spinner-color="primary" />
    </div>

    <div v-else-if="loadError" class="account-empty" role="alert">
      <v-icon class="account-empty-icon">mdi-alert-circle-outline</v-icon>
      <h2>We couldn't load your account</h2>
      <p>{{ loadError }}</p>
      <v-btn
        class="mt-3"
        color="primary"
        variant="flat"
        rounded="pill"
        prepend-icon="mdi-refresh"
        @click="loadData"
      >
        Try again
      </v-btn>
    </div>

    <template v-else>
      <div v-if="statusError" class="account-status account-status--error" role="alert">
        <v-icon class="account-status-icon">mdi-alert-circle</v-icon>
        {{ statusError }}
      </div>
      <div
        v-else-if="statusSuccess"
        class="account-status account-status--success"
        role="status"
      >
        <v-icon class="account-status-icon">mdi-check-circle</v-icon>
        {{ statusSuccess }}
      </div>

      <div class="account-grid">
        <section class="account-card">
          <h2 class="account-card-title">Account</h2>

          <div class="account-row">
            <span class="account-row-label">Signed in as</span>
            <span class="account-row-value">{{ email || "-" }}</span>
          </div>

          <div class="account-row">
            <span class="account-row-label">Role</span>
            <span
              class="account-row-value account-role-pill"
              :class="rolePillClass"
            >
              {{ role }}
            </span>
          </div>

          <div class="account-row">
            <span class="account-row-label">Customer sign-up link</span>
            <span class="account-row-value account-row-value--mono">{{ signupUrl }}</span>
          </div>

          <div class="account-actions">
            <v-btn
              variant="flat"
              color="primary"
              rounded="pill"
              :prepend-icon="copyState.link === 'copied' ? 'mdi-check' : 'mdi-content-copy'"
              @click="onCopyLink"
            >
              {{ copyState.link === "copied" ? "Copied" : "Copy customer sign-up link" }}
            </v-btn>
            <v-btn
              variant="flat"
              color="primary"
              rounded="pill"
              prepend-icon="mdi-lock-reset"
              :loading="resetting"
              :disabled="resetting"
              @click="onResetPassword"
            >
              Reset password
            </v-btn>
            <v-btn
              variant="outlined"
              color="primary"
              rounded="pill"
              prepend-icon="mdi-logout"
              :loading="signingOut"
              :disabled="signingOut"
              @click="onSignOut"
            >
              Sign out
            </v-btn>
          </div>
        </section>

        <section class="account-card">
          <h2 class="account-card-title">Billing</h2>
          <p class="account-billing-status">
            <v-icon class="account-billing-icon">
              {{ isActive ? "mdi-check-decagram" : "mdi-clock-outline" }}
            </v-icon>
            <span>
              <strong>{{
                isActive ? "Subscription active." : "Activation pending."
              }}</strong>
              {{
                isActive
                  ? "Thanks for being a Lulu Rewards business."
                  : "We're processing your first payment - this usually finishes within a few minutes."
              }}
            </span>
          </p>
          <template v-if="isOwner">
            <p class="account-billing-help">
              To update your payment method or cancel your subscription in the
              Stripe customer portal - opens in a new tab. If you would like to
              make changes to your plan, please
              <button
                type="button"
                class="account-inline-link"
                @click="onOpenContact('plan', $event)"
              >contact us</button>.
            </p>
            <v-btn
              class="account-billing-manage"
              variant="flat"
              color="primary"
              rounded="pill"
              prepend-icon="mdi-open-in-new"
              @click="onManageBilling"
            >
              Manage billing
            </v-btn>
          </template>
          <p v-else class="account-billing-help">
            Only the business owner can manage billing in the Stripe customer
            portal.
          </p>
        </section>

        <section class="account-card">
          <h2 class="account-card-title">Support</h2>
          <p class="account-billing-help">
            Got a question, a bug to report, or an idea that would make Lulu
            better? We read every message.
          </p>
          <div class="account-actions">
            <v-btn
              ref="contactBtnRef"
              variant="outlined"
              color="primary"
              rounded="pill"
              prepend-icon="mdi-email-outline"
              @click="onOpenContact('support', $event)"
            >
              Contact us
            </v-btn>
            <v-btn
              ref="feedbackBtnRef"
              variant="outlined"
              color="primary"
              rounded="pill"
              prepend-icon="mdi-message-text-outline"
              @click="onOpenContact('feedback', $event)"
            >
              Share feedback
            </v-btn>
          </div>
        </section>

        <section class="account-card">
          <h2 class="account-card-title">Data</h2>
          <p class="account-billing-help">
            Download your raw stamp activity and customer details as an Excel
            workbook - one tab for every stamp, one for your customers including
            each customer's marketing email consent.
          </p>
          <div class="account-actions">
            <v-btn
              variant="flat"
              color="primary"
              rounded="pill"
              prepend-icon="mdi-download"
              :loading="exporting"
              :disabled="exporting"
              @click="onExportRawData"
            >
              Export raw data
            </v-btn>
          </div>
        </section>
      </div>
    </template>

    <ContactDialog
      v-model="contactOpen"
      :mode="contactMode"
      :initial-message="contactInitialMessage"
      :submitting="contactSubmitting"
      :error-message="contactError"
      :success="contactSuccess"
      :return-focus-to="contactReturnFocusTo"
      @submit="onContactSubmit"
      @cancel="onContactCancel"
      @close="onContactClose"
    />
  </div>
</template>

<script>
import { computed, inject, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import DashboardPageHeader from "@/components/Molecules/DashboardPageHeader.vue";
import BrandedLoader from "@/components/Molecules/BrandedLoader.vue";
import ContactDialog from "@/components/Organisms/ContactDialog.vue";
import {
  authStore,
  sendOwnerResetEmail,
  signOut,
} from "@/stores/authStore.js";
import {
  getCurrentUserRole,
  getBusinessIdActiveStatus,
  getBusinessDisplayName,
  getEmployeesData,
} from "@/firebase-business.js";
import { exportRawData } from "@/dataExport.js";
import { sendContactMessage } from "@/emailSender.js";
import { buildPlanChangeMessage } from "@/utils/pricingPlans.js";
import { track } from "@/analytics.js";

// Mirrors iOS `ContactViewModel.getNameForUserWith` fallback. Kept verbatim so
// downstream analytics aggregate cleanly across platforms.
const UNKNOWN_NAME_FALLBACK = "Unknown name: getNameForUserWith() failed";

export default {
  name: "DashboardAccountComponent",
  components: { DashboardPageHeader, BrandedLoader, ContactDialog },
  setup() {
    const router = useRouter();
    const selectedBusinessId = inject("selectedBusinessId");

    const loading = ref(true);
    const loadError = ref("");
    const role = ref("Employee");
    const isActive = ref(false);
    const resetting = ref(false);
    const signingOut = ref(false);
    const exporting = ref(false);
    const statusError = ref("");
    const statusSuccess = ref("");
    const copyState = reactive({ link: "idle" });

    const contactOpen = ref(false);
    const contactMode = ref("support");
    const contactInitialMessage = ref("");
    const contactSubmitting = ref(false);
    const contactError = ref("");
    const contactSuccess = ref(false);
    const contactReturnFocusTo = ref(null);

    const email = computed(() => authStore.user?.email || "");

    const signupUrl = computed(() => {
      const id = selectedBusinessId?.value || "";
      return id
        ? `https://lulurewards.co.uk/business/${id}`
        : "https://lulurewards.co.uk/business/";
    });

    const isOwner = computed(() => role.value === "Owner");

    const rolePillClass = computed(() => {
      if (role.value === "Owner") return "account-role-pill--strong";
      if (role.value === "Administrator") return "account-role-pill--medium";
      return "account-role-pill--soft";
    });

    const loadData = async () => {
      const id = selectedBusinessId?.value;
      loading.value = true;
      loadError.value = "";
      try {
        const [resolvedRole, active] = await Promise.all([
          id ? getCurrentUserRole(id, email.value) : Promise.resolve("Employee"),
          id ? getBusinessIdActiveStatus(id) : Promise.resolve(false),
        ]);
        role.value = resolvedRole || "Employee";
        isActive.value = !!active;
        if (id) track("dashboard_account_viewed", { business_id: id });
      } catch (e) {
        loadError.value =
          "Something went wrong loading your account. Please try again in a moment.";
      } finally {
        loading.value = false;
      }
    };

    onMounted(loadData);
    watch(() => selectedBusinessId?.value, loadData);

    const flashSuccess = (message) => {
      statusError.value = "";
      statusSuccess.value = message;
      setTimeout(() => {
        if (statusSuccess.value === message) statusSuccess.value = "";
      }, 4000);
    };

    const flashError = (message) => {
      statusSuccess.value = "";
      statusError.value = message;
    };

    const onCopyLink = async () => {
      const value = signupUrl.value;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = value;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        copyState.link = "copied";
        setTimeout(() => {
          copyState.link = "idle";
        }, 2000);
        track("dashboard_account_link_copied", {
          business_id: selectedBusinessId?.value,
        });
      } catch (e) {
        copyState.link = "idle";
      }
    };

    const onResetPassword = async () => {
      if (!email.value) {
        flashError("We couldn't find your email address. Please sign in again.");
        return;
      }
      resetting.value = true;
      try {
        await sendOwnerResetEmail(email.value);
        flashSuccess(`We've sent password reset instructions to ${email.value}.`);
        track("dashboard_account_password_reset_sent", {});
      } catch (e) {
        flashError(
          (e && e.message) ||
            "We couldn't send the reset email. Please try again in a moment."
        );
      } finally {
        resetting.value = false;
      }
    };

    const onSignOut = async () => {
      signingOut.value = true;
      try {
        track("dashboard_account_sign_out", {});
        await signOut();
        router.push("/business-signin");
      } catch (e) {
        signingOut.value = false;
        flashError("We couldn't sign you out. Please try again.");
      }
    };

    const onManageBilling = () => {
      track("dashboard_account_manage_billing_clicked", {
        business_id: selectedBusinessId?.value,
      });
      window.open(
        "https://billing.stripe.com/p/login/6oUdR9dwY0rjb7BcJR8og00",
        "_blank",
        "noopener,noreferrer",
      );
    };

    const onExportRawData = async () => {
      const id = selectedBusinessId?.value;
      if (!id) {
        flashError("No business selected.");
        return;
      }
      exporting.value = true;
      try {
        const name = await getBusinessDisplayName(id);
        const result = await exportRawData(id, name);
        if (result.success) {
          flashSuccess(result.message);
          track("dashboard_account_export_raw_data", { business_id: id });
        } else {
          flashError(result.message);
        }
      } catch (e) {
        flashError("We couldn't export your data. Please try again in a moment.");
      } finally {
        exporting.value = false;
      }
    };

    const resolveSenderName = async () => {
      const id = selectedBusinessId?.value;
      const userEmail = email.value;
      if (!id || !userEmail) return UNKNOWN_NAME_FALLBACK;
      try {
        const list = await getEmployeesData(id);
        const match = (list || []).find(
          (row) =>
            String(row.email || "").toLowerCase() === userEmail.toLowerCase()
        );
        const name = match && String(match.name || "").trim();
        return name || UNKNOWN_NAME_FALLBACK;
      } catch (e) {
        track("dashboard_contact_name_lookup_failed", { business_id: id });
        return UNKNOWN_NAME_FALLBACK;
      }
    };

    const CONTACT_MODES = ["support", "feedback", "plan"];

    const onOpenContact = (mode, event) => {
      contactMode.value = CONTACT_MODES.includes(mode) ? mode : "support";
      // Plan changes open with a prefilled message; the other modes start blank.
      contactInitialMessage.value =
        contactMode.value === "plan"
          ? buildPlanChangeMessage({ businessId: selectedBusinessId?.value })
          : "";
      contactError.value = "";
      contactSuccess.value = false;
      contactReturnFocusTo.value =
        (event && event.currentTarget) || document.activeElement || null;
      contactOpen.value = true;
      track("dashboard_contact_opened", {
        type: contactMode.value,
        business_id: selectedBusinessId?.value || "",
      });
    };

    const onContactSubmit = async (payload) => {
      if (!email.value) {
        contactError.value =
          "We couldn't find your email address. Please sign in again.";
        return;
      }
      contactSubmitting.value = true;
      contactError.value = "";
      try {
        const senderName = await resolveSenderName();
        await sendContactMessage({
          name: senderName,
          email: email.value,
          title: payload.title,
          message: payload.message,
        });
        contactSuccess.value = true;
        track("dashboard_contact_submitted", {
          type: payload.mode,
          business_id: selectedBusinessId?.value || "",
        });
      } catch (e) {
        contactError.value =
          "We couldn't send your message. Check your connection and try again, or email hello@lulurewards.co.uk.";
        track("dashboard_contact_failed", {
          type: payload.mode,
          business_id: selectedBusinessId?.value || "",
        });
      } finally {
        contactSubmitting.value = false;
      }
    };

    const onContactCancel = () => {
      contactError.value = "";
    };

    const onContactClose = () => {
      const wasSuccess = contactSuccess.value;
      const mode = contactMode.value;
      contactSuccess.value = false;
      contactError.value = "";
      if (wasSuccess) {
        flashSuccess(
          mode === "feedback"
            ? "Thanks - your feedback is on its way."
            : "Thanks - your message is on its way."
        );
      }
    };

    return {
      loading,
      loadError,
      email,
      role,
      isOwner,
      rolePillClass,
      isActive,
      signupUrl,
      copyState,
      resetting,
      signingOut,
      exporting,
      onExportRawData,
      statusError,
      statusSuccess,
      loadData,
      onCopyLink,
      onResetPassword,
      onSignOut,
      onManageBilling,
      contactOpen,
      contactMode,
      contactInitialMessage,
      contactSubmitting,
      contactError,
      contactSuccess,
      contactReturnFocusTo,
      onOpenContact,
      onContactSubmit,
      onContactCancel,
      onContactClose,
    };
  },
};
</script>

<style scoped>
.account-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.account-page :deep(.v-btn) {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-radius: 999px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Primary green pill - matches the header "Get started" button. */
.account-page :deep(.v-btn--variant-flat.text-primary),
.account-page :deep(.v-btn.bg-primary),
.account-page :deep(.v-btn--variant-flat[class*="text-primary"]) {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
}

.account-page :deep(.v-btn--variant-flat.text-primary:hover),
.account-page :deep(.v-btn.bg-primary:hover),
.account-page :deep(.v-btn--variant-flat[class*="text-primary"]:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

/* Secondary outlined buttons - keep hierarchy but match the hover pop. */
.account-page :deep(.v-btn--variant-outlined.text-primary) {
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.1) !important;
}

.account-page :deep(.v-btn--variant-outlined.text-primary:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.2) !important;
  transform: translateY(-1px);
}

/* Destructive (Sign out / error outlined) - same pop with red tint. */
.account-page :deep(.v-btn--variant-outlined.text-error) {
  box-shadow: 0 2px 8px rgba(176, 0, 32, 0.1) !important;
}

.account-page :deep(.v-btn--variant-outlined.text-error:hover) {
  box-shadow: 0 4px 16px rgba(176, 0, 32, 0.25) !important;
  transform: translateY(-1px);
}

.account-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.account-empty {
  text-align: center;
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 48px 24px;
}

.account-empty-icon {
  color: var(--lulu-danger) !important;
  font-size: 48px !important;
  margin-bottom: 12px;
}

.account-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
}

.account-status--success {
  background: var(--lulu-primary-soft);
  color: var(--lulu-primary);
}

.account-status--error {
  background: var(--lulu-danger-soft);
  color: var(--lulu-danger);
}

.account-status-icon {
  font-size: 16px !important;
}

.account-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;
}

@media (min-width: 960px) {
  .account-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.account-card {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.account-card-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  margin: 0 0 4px;
  text-transform: none;
  letter-spacing: 0.04em;
}

.account-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.account-row-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.04em;
  color: var(--lulu-text-muted);
}

.account-row-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--lulu-text-strong);
  word-break: break-all;
}

.account-row-value--mono {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-weight: 500;
  font-size: 13px;
}

.account-role-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.04em;
  padding: 4px 10px;
  border-radius: 999px;
  width: max-content;
}

/* Strong = Owner. Highest authority - solid primary fill, white text. */
.account-role-pill--strong {
  background: var(--lulu-primary);
  color: #ffffff;
}

/* Medium = Administrator. Brand-flavoured soft fill, primary text. */
.account-role-pill--medium {
  background: var(--lulu-primary-soft);
  color: var(--lulu-primary);
}

/* Soft = Employee. Neutral surface so the brand colour stays meaningful
   for the elevated roles only. */
.account-role-pill--soft {
  background: var(--lulu-surface-muted);
  color: var(--lulu-text-strong);
}

.account-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
}

.account-billing-status {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: var(--lulu-text-strong);
  margin: 0;
  line-height: 1.5;
}

.account-billing-icon {
  color: var(--lulu-primary) !important;
  margin-top: 2px;
  flex-shrink: 0;
}

.account-billing-help {
  font-size: 13px;
  color: var(--lulu-text-muted);
  line-height: 1.5;
  margin: 0;
}

/* A button that reads as an inline link: it opens the contact dialog rather
   than navigating, so it stays a button for keyboard and screen-reader users. */
.account-inline-link {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--lulu-link);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.account-inline-link:hover,
.account-inline-link:focus-visible {
  color: var(--lulu-primary-dark);
}

.account-inline-link:focus-visible {
  outline: 2px solid var(--lulu-primary);
  outline-offset: 2px;
  border-radius: 2px;
}

/* Push Manage billing to the bottom of the card so it lines up vertically
   with the Reset password / actions row in the Account card next door. */
.account-billing-manage {
  margin-top: auto;
  align-self: flex-start;
}
</style>
